import { ItemType } from 'antd/lib/menu/hooks/useItems';
import { ColumnsType } from 'antd/lib/table';
import { Button, FilterIcon, Table } from 'fave-ui';
import { Plus } from 'phosphor-react';
import React, { useState } from 'react';
import { useNavigate, useParams } from 'react-router';
import { PageHeaderWithSpace } from '../../components/common/PageHeaderWithSpace/PageHeaderWithSpace';
import { RenderColumn } from '../../helpers/tableHelpers/RenderColumn';
import { defaultTableConfig } from '../../helpers/defaults';
import {
  FetchFavePassesDataResponse,
  useFetchFavePasses,
} from '../../services/FavePasses/useFetchFavePasses';
import { TableSettingsType } from '../../types/configTypes';
import {
  calcTableHeight,
  calcTableWidth,
  createOnTableChangeHandler,
  formatFilter,
} from '../../utils/utilFunctions';
import style from './style.module.css';
import { useUserContext } from '../../contexts/UserContext';
import LockPage from '../../components/LockPage';

type FavePassesViewProps = {};

const tableHeight = calcTableHeight();
const tableWidth = calcTableWidth();

export const FavePassesView: React.FC<FavePassesViewProps> = () => {
  const { getPageSettings } = useUserContext();
  const pageSettings = getPageSettings('partners');
  const enableViewFavePassesList = !!pageSettings?.show_fave_passes;
  const enableUpdateFavePasses = !!pageSettings?.update_fave_passes;
  const { id } = useParams();
  const [tableConfig, setTableConfig] =
    useState<TableSettingsType>(defaultTableConfig);
  const { data: favePassesData, isFetching } = useFetchFavePasses({
    page: tableConfig.page,
    company_id: parseInt(String(id)),
  });
  const navigate = useNavigate();

  const tableChangeHandler = createOnTableChangeHandler(
    setTableConfig,
    filters => formatFilter([{ key: 'status', value: filters.status }]),
  );

  const menuItems = (pass_id: number): ItemType[] => [
    {
      key: '1',
      label: enableUpdateFavePasses ? 'View / Edit' : 'View',
      onClick: () =>
        navigate(`/admin/partners/${id}/fave_passes/${pass_id}/edit`),
    },
    {
      key: '2',
      label: 'Outlet Settings',
      onClick: () =>
        navigate(
          `/admin/partners/${id}/fave_passes/${pass_id}/outlet_settings`,
        ),
    },
  ];

  const favePassesColumns: ColumnsType<FetchFavePassesDataResponse> = [
    {
      title: 'ID',
      dataIndex: 'id',
      width: 50,
      render: RenderColumn.rowText,
    },
    {
      title: 'Name',
      dataIndex: 'name',
      filterIcon: FilterIcon,
      width: 200,
      render: RenderColumn.rowText,
    },
    {
      title: 'Total Sessions',
      dataIndex: 'total_sessions',
      width: 100,
      render: RenderColumn.rowText,
    },
    {
      title: 'Validity',
      dataIndex: 'validity',
      width: 100,
      render: RenderColumn.rowText,
    },
    {
      title: 'Actions',
      key: 'action',
      width: 75,
      render: ({ id }: FetchFavePassesDataResponse) =>
        RenderColumn.dropdownMenu(menuItems(id)),
      fixed: 'right',
    },
  ];

  return enableViewFavePassesList ? (
    <div className={style.favePassesView}>
      <PageHeaderWithSpace
        title={'Fave Passes'}
        additionalContent={
          enableUpdateFavePasses && (
            <div className={style.buttonGroup}>
              <Button
                size={'large'}
                type={'primary'}
                icon={<Plus />}
                shape={'circle'}
                onClick={() =>
                  navigate(`/admin/partners/${id}/fave_passes/create`)
                }
              />
            </div>
          )
        }
      />
      <Table
        rowKey={row => row.id}
        dataSource={favePassesData?.fave_passes}
        columns={favePassesColumns}
        onChange={tableChangeHandler}
        loading={isFetching}
        scroll={{
          x: tableWidth,
          y: tableHeight,
        }}
        pagination={{
          total: favePassesData?.count,
          position: ['bottomCenter'],
          pageSize: tableConfig.pageSize,
          current: tableConfig.page,
          showSizeChanger: false,
        }}
      />
    </div>
  ) : (
    <LockPage />
  );
};
